import React, {useState} from 'react';
import {EmailNotificationStep, TranslationStep, Workflow} from '@/types/workflow';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faEllipsis,
    faEnvelope,
    faLanguage,
    faSort,
    faSortDown,
    faSortUp,
    faStar
} from '@fortawesome/free-solid-svg-icons';
import {usePage} from '@inertiajs/react';
import {PageProps} from '@/types';
import Dropdown from "@/Components/Dropdown";

type SortField = 'name' | 'created_at' | 'default';
type SortDirection = 'asc' | 'desc';

interface WorkflowTableProps {
    workflows: Workflow[];
    onEdit: (workflow: Workflow) => void;
    onDelete: (workflow: Workflow) => void;
}

export default function WorkflowTable({ workflows, onEdit, onDelete }: WorkflowTableProps) {
    const { auth } = usePage<PageProps>().props;
    const isPaidUser = !auth.user.is_trial;
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedWorkflows = [...workflows].sort((a, b) => {
        let aValue: any, bValue: any;

        switch (sortField) {
            case 'name':
                aValue = a.name.toLowerCase();
                bValue = b.name.toLowerCase();
                break;
            case 'created_at':
                aValue = new Date(a.created_at || 0);
                bValue = new Date(b.created_at || 0);
                break;
            case 'default':
                aValue = a.isDefault ? 1 : 0;
                bValue = b.isDefault ? 1 : 0;
                break;
            default:
                return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return faSort;
        return sortDirection === 'asc' ? faSortUp : faSortDown;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderStepsSummary = (workflow: Workflow) => {
        const workflowSteps = Array.isArray(workflow.steps) ? workflow.steps : [];
        const translationStep = workflowSteps.find(step => step.type === 'translation') as TranslationStep;
        const emailStep = workflowSteps.find(step => step.type === 'email_notification') as EmailNotificationStep;

        const steps = [];

        if (translationStep) {
            const languageCount = translationStep.config.target_languages.length;
            steps.push(
                <span key="translation" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 mr-2">
                    <FontAwesomeIcon icon={faLanguage} className="w-3 h-3 mr-1" />
                    {languageCount} language{languageCount !== 1 ? 's' : ''}
                </span>
            );
        }

        if (emailStep) {
            const recipientCount = emailStep.config.recipients.length;
            steps.push(
                <span key="email" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                    <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3 mr-1" />
                    {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
                </span>
            );
        }

        return <div className="flex flex-wrap gap-1">{steps}</div>;
    };

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                                onClick={() => handleSort('name')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Name</span>
                                    <FontAwesomeIcon icon={getSortIcon('name')} className="w-3 h-3" />
                                </div>
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                                Steps
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-widerhover:bg-gray-100 dark:hover:bg-gray-600"
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Executions</span>

                                </div>
                            </th>
                            <th
                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 hidden lg:table-cell"
                                onClick={() => handleSort('created_at')}
                            >
                                <div className="flex items-center space-x-1">
                                    <span>Created</span>
                                    <FontAwesomeIcon icon={getSortIcon('created_at')} className="w-3 h-3" />
                                </div>
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">

                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedWorkflows.map((workflow) => (
                            <tr
                                key={workflow.id}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors "
                                onClick={() => onEdit(workflow)}
                            >
                                <td className="px-6 py-3 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div>
                                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center">
                                                {workflow.name}
                                                {workflow.isDefault && (
                                                    <FontAwesomeIcon
                                                        icon={faStar}
                                                        className="w-4 h-4 ml-2 text-yellow-300"
                                                        title="Default workflow"
                                                    />
                                                )}
                                            </div>


                                            <div className="md:hidden mt-2">
                                                {renderStepsSummary(workflow)}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap hidden md:table-cell">
                                    {renderStepsSummary(workflow)}
                                </td>
                                <td className="px-6 py-3 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">
                                    {workflow.executions_count ? <span>{workflow.executions_count}</span> : <span className="text-gray-400 dark:text-gray-500">None</span>}
                                </td>
                                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden lg:table-cell">
                                    {workflow.created_at && formatDate(workflow.created_at)}
                                </td>
                                <td
                                    className="px-6 py-3 whitespace-nowrap text-right text-sm"
                                    onClick={(e) => e.stopPropagation()} // Prevent row click when clicking delete
                                >


                                    <div className="flex items-center justify-end  font-medium">
                                        <Dropdown>
                                            <Dropdown.Trigger>
                                                {
                                                    (isOpen) => (
                                                        <button
                                                            type="button"
                                                            className=" menu-button flex text-sm leading-4 font-medium rounded-md text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none transition ease-in-out duration-150"
                                                        >
                                                            <FontAwesomeIcon
                                                                className={`align-middle w-4 h-4 items-center p-2 text-sm font-medium text-center text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-4 focus:outline-none ${isOpen ? 'bg-gray-100 dark:bg-gray-700' : 'bg-white dark:bg-gray-800'}`}
                                                                icon={faEllipsis}
                                                            />
                                                        </button>
                                                    )
                                                }
                                            </Dropdown.Trigger>
                                            <Dropdown.Content className="sm:mt-1">
                                                <Dropdown.Item
                                                    title="Edit"
                                                    onClick={() => onEdit(workflow)}>
                                                    <span>Edit</span>
                                                </Dropdown.Item>
                                                <Dropdown.Item
                                                    title="Download"
                                                    onClick={() => onDelete(workflow)}>

                                                    <span>Delete</span>
                                                </Dropdown.Item>
                                            </Dropdown.Content>
                                        </Dropdown>

                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {workflows.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-gray-500 dark:text-gray-400 text-sm">No workflows found</div>
                </div>
            )}
        </div>
    );
}
